import { Area } from 'react-easy-crop/types';

export default class ImageUtils {
  static notFoundImageGradient = 'linear-gradient(45deg,#2fc3e4,#41cbce 19.47%,#41cbce 78.03%,#00b7b0)';
  static imageOrNotFoundGradient(imageUrl: string): string {
    return imageUrl ? `url("${imageUrl}")` : ImageUtils.notFoundImageGradient;
  }

  static async getCroppedImage(imageSrc: string, pixelCrop: Area, rotation = 0): Promise<string> {
    const image = await this.createImage(imageSrc);
    if (!image) {
      return null;
    }
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const safeArea = Math.max(image.width, image.height) * 2;

    // set each dimensions to double largest dimension to allow for a safe area for the
    // image to rotate in without being clipped by canvas context
    canvas.width = safeArea;
    canvas.height = safeArea;

    // translate canvas context to a central location on image to allow rotating around the center.
    ctx.translate(safeArea / 2, safeArea / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-safeArea / 2, -safeArea / 2);

    // draw rotated image and store data.
    ctx.drawImage(image, safeArea / 2 - image.width * 0.5, safeArea / 2 - image.height * 0.5);
    const data = ctx.getImageData(0, 0, safeArea, safeArea);

    // set canvas width to final desired crop size - this will clear existing context
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // paste generated rotate image with correct offsets for x,y crop values.
    ctx.putImageData(
      data,
      0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x,
      0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y
    );

    return canvas.toDataURL('image/jpeg');
  }

  // FROM https://stackoverflow.com/a/32490603/4562693
  public static getOrientation(file: File, callback: (orientation: number) => void) {
    const reader = new FileReader();

    reader.onload = function (event) {
      if (!event.target) {
        return;
      }

      const file = event.target;
      const view = new DataView(file.result as ArrayBuffer);

      if (view.getUint16(0, false) != 0xffd8) {
        return callback(-2);
      }

      const length = view.byteLength;
      let offset = 2;

      while (offset < length) {
        if (view.getUint16(offset + 2, false) <= 8) return callback(-1);
        const marker = view.getUint16(offset, false);
        offset += 2;

        if (marker == 0xffe1) {
          if (view.getUint32((offset += 2), false) != 0x45786966) {
            return callback(-1);
          }

          const little = view.getUint16((offset += 6), false) == 0x4949;
          offset += view.getUint32(offset + 4, little);
          const tags = view.getUint16(offset, little);
          offset += 2;
          for (let i = 0; i < tags; i++) {
            if (view.getUint16(offset + i * 12, little) == 0x0112) {
              return callback(view.getUint16(offset + i * 12 + 8, little));
            }
          }
        } else if ((marker & 0xff00) != 0xff00) {
          break;
        } else {
          offset += view.getUint16(offset, false);
        }
      }
      return callback(-1);
    };

    reader.readAsArrayBuffer(file);
  }
  // FROM https://stackoverflow.com/a/40867559/4562693
  public static fixOrientation(orientation: number, image: HTMLImageElement, onLoad: () => void) {
    if (orientation <= 0) {
      onLoad();
      return;
    }
    const width = image.naturalWidth,
      height = image.naturalHeight,
      canvas = document.createElement('canvas'),
      ctx = canvas.getContext('2d');

    // set proper canvas dimensions before transform & export
    if (4 < orientation && orientation < 9) {
      canvas.width = height;
      canvas.height = width;
    } else {
      canvas.width = width;
      canvas.height = height;
    }

    // transform context before drawing image
    switch (orientation) {
      case 2:
        ctx.transform(-1, 0, 0, 1, width, 0);
        break;
      case 3:
        ctx.transform(-1, 0, 0, -1, width, height);
        break;
      case 4:
        ctx.transform(1, 0, 0, -1, 0, height);
        break;
      case 5:
        ctx.transform(0, 1, 1, 0, 0, 0);
        break;
      case 6:
        ctx.transform(0, 1, -1, 0, height, 0);
        break;
      case 7:
        ctx.transform(0, -1, -1, 0, height, width);
        break;
      case 8:
        ctx.transform(0, -1, 1, 0, 0, width);
        break;
      default:
        break;
    }

    // draw image
    ctx.drawImage(image, 0, 0);
    image.src = canvas.toDataURL();
    image.onload = onLoad;
  }

  public static resizeToCanvas(image: HTMLImageElement, maxResolution: number): HTMLCanvasElement {
    // Resize and encode as JPG format
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const maxImage = Math.max(image.naturalWidth, image.naturalHeight);
    const newMax = Math.min(maxImage, maxResolution);
    const newScale = newMax / maxImage;

    canvas.width = image.naturalWidth * newScale;
    canvas.height = image.naturalHeight * newScale;

    ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, 0, 0, canvas.width, canvas.height);
    return canvas;
  }
  public static canvasToJpegBase64(canvas: HTMLCanvasElement) {
    return canvas.toDataURL('image/jpeg');
  }

  public static createImage = (url: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', _ => reject(null));
      image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues on CodeSandbox
      image.src = url;
    });
}
